import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../Api';

const AuthContext = createContext();

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	// Check if user is logged in on mount
	useEffect(() => {
		checkAuth();
	}, []);

	const checkAuth = async () => {
		const token = localStorage.getItem('tocken');

		if (!token) {
			setIsAuthenticated(false);
			setUser(null);
			setLoading(false);
			return null;
		}

		// Check if user data is cached in localStorage
		const cachedUser = localStorage.getItem('user_data');
		if (cachedUser) {
			try {
				const userData = JSON.parse(cachedUser);
				setUser(userData);
				setIsAuthenticated(true);
				setLoading(false);
				return userData;
			} catch (e) {
				// If cached data is corrupted, fetch fresh data
			}
		}

		// Fetch user data from API
		try {
			const AuthStr = 'Bearer ' + token;
			const res = await API.get('me', { headers: { Authorization: AuthStr } });

			const userData = res.data;
			setUser(userData);
			setIsAuthenticated(true);

			// Cache user data in localStorage
			localStorage.setItem('user_data', JSON.stringify(userData));

			setLoading(false);
			return userData;
		} catch (error) {
			if (error.response?.status === 401) {
				// Token is invalid, clear everything
				logout();
			}
			setLoading(false);
			return null;
		}
	};

	const logout = () => {
		localStorage.removeItem('tocken');
		localStorage.removeItem('user_data');
		setUser(null);
		setIsAuthenticated(false);
	};

	// Refresh user data (call this after profile updates)
	const refreshUser = async () => {
		localStorage.removeItem('user_data'); // Clear cache
		return await checkAuth();
	};

	const value = {
		user,
		loading,
		isAuthenticated,
		checkAuth,
		logout,
		refreshUser
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
